import { 
    signInWithPopup, 
    signInWithRedirect, 
    getRedirectResult,
    GoogleAuthProvider 
} from 'firebase/auth';

/**
 * Enhanced Google Sign-in with fallback for third-party cookie restrictions
 */
export class EnhancedAuthService {
    constructor(auth) {
        this.auth = auth;
        this.provider = new GoogleAuthProvider();
        
        // Configure Google provider
        this.provider.addScope('email');
        this.provider.addScope('profile');
        
        // Set custom parameters
        this.provider.setCustomParameters({
            prompt: 'select_account'
        });
    }

    /**
     * Try popup sign-in first, fallback to redirect if blocked
     */
    async signInWithGoogle() {
        try {
            // Check if we're returning from a redirect
            const redirectResult = await getRedirectResult(this.auth);
            if (redirectResult) {
                return redirectResult;
            }

            // Try popup first
            return await this.tryPopupSignIn();
        } catch (error) {
            console.error('Google sign-in error:', error);
            throw error;
        }
    }

    async tryPopupSignIn() {
        try {
            console.log('Attempting popup sign-in...');
            const result = await signInWithPopup(this.auth, this.provider);
            console.log('Popup sign-in successful');
            return result;
        } catch (error) {
            console.warn('Popup sign-in failed:', error.code, error.message);
            
            // Check if it's a popup-related error
            if (this.isPopupError(error)) {
                console.log('Falling back to redirect sign-in...');
                return await this.tryRedirectSignIn();
            }
            
            throw error;
        }
    }

    async tryRedirectSignIn() {
        try {
            await signInWithRedirect(this.auth, this.provider);
            // Note: signInWithRedirect doesn't return a result immediately
            // The result will be available after redirect via getRedirectResult
            return null;
        } catch (error) {
            console.error('Redirect sign-in failed:', error);
            throw error;
        }
    }

    isPopupError(error) {
        const popupErrorCodes = [
            'auth/popup-blocked',
            'auth/popup-closed-by-user',
            'auth/cancelled-popup-request',
            'auth/unauthorized-domain'
        ];
        return popupErrorCodes.includes(error.code);
    }

    /**
     * Check for redirect result on app initialization
     */
    async handleRedirectResult() {
        try {
            const result = await getRedirectResult(this.auth);
            if (result) {
                console.log('Redirect sign-in completed');
                return result;
            }
            return null;
        } catch (error) {
            console.error('Error handling redirect result:', error);
            throw error;
        }
    }
}

/**
 * Utility function to detect if third-party cookies are blocked
 */
export const detectThirdPartyCookieSupport = async () => {
    return new Promise((resolve) => {
        // Create a test iframe to check third-party cookie support
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        iframe.src = 'https://accounts.google.com/gsi/cookie';
        
        const timeout = setTimeout(() => {
            cleanup();
            resolve(false); // Assume blocked if no response
        }, 3000);

        const cleanup = () => {
            clearTimeout(timeout);
            if (iframe.parentNode) {
                iframe.parentNode.removeChild(iframe);
            }
        };

        iframe.onload = () => {
            cleanup();
            resolve(true); // Cookies likely supported
        };

        iframe.onerror = () => {
            cleanup();
            resolve(false); // Cookies likely blocked
        };

        document.body.appendChild(iframe);
    });
};

/**
 * Show user-friendly message about cookie requirements
 */
export const showCookieRequiredMessage = () => {
    const message = `
        การเข้าสู่ระบบด้วย Google ต้องการการรองรับ cookies
        
        วิธีแก้ไข:
        1. เปิดใช้งาน third-party cookies ในเบราว์เซอร์
        2. เพิ่ม fastlanelv.com ในรายการ exceptions
        3. ลองใช้เบราว์เซอร์อื่น
        
        หรือติดต่อผู้ดูแลระบบเพื่อขอความช่วยเหลือ
    `;
    
    return message;
};

export default EnhancedAuthService;

// Create and export a lazy singleton instance
let authServiceInstance = null;

export const getAuthService = () => {
    if (!authServiceInstance) {
        // Import auth here to avoid circular dependencies
        const { auth } = require('../firebase');
        authServiceInstance = new EnhancedAuthService(auth);
    }
    return authServiceInstance;
};
